import { useQuery } from 'react-query';

import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';

import type { RelatedOrganization } from '../_types';
import type { ApiCollectionResponse } from 'types/api_response';

/**
 * Fetches risk pool organizations for a given organization ID
 */
export const getRiskPoolOrganizations = async (
  organizationId: string,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ApiCollectionResponse<RelatedOrganization>> => {
  const url = `${baseApiUrl()}/lexid/related-orgs/v1/related-organizations/${organizationId}`;

  try {
    const data: RelatedOrganization[] | ApiCollectionResponse<RelatedOrganization> = await fetcher({
      url,
      options: { signal },
    });

    if (Array.isArray(data)) {
      return {
        content: data,
        count: data.length,
      };
    }

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

/**
 * Hook to fetch and cache risk pool organizations using React Query
 */
export default function useRiskPoolOrganizations(
  organizationId?: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<ApiCollectionResponse<RelatedOrganization>, ServerErrorResponse>(
    ['riskPoolOrganizations', organizationId],
    async ({ signal }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      return getRiskPoolOrganizations(organizationId, signal, onError);
    },
    {
      enabled: !!organizationId,
      keepPreviousData: true,
      ...options,
    }
  );
}
